// #607 F2: Abschnitt „Freigaben“ in der Dokument-Vorschau (ersetzt die beiden Schalter Mieter/Eigentümer).
// Zeigt, an wen das Dokument im Portal freigegeben ist (mit Datum und wer), wer es geöffnet hat,
// und öffnet den Freigabe-Dialog (Freigeben / Ändern) bzw. entzieht alle Freigaben.
import { useCallback, useEffect, useState } from 'react';
import { Eye, Globe, LockSimple } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton } from '../immobilien/ui';
import { supabase } from '../../lib/supabase/client';
import { deDatum, entziehe, ladeFreigabeAnzeige, meldeFreigabenGeaendert, type FreigabeAnzeige, type FreigabeDok } from '../../lib/dokumente/freigaben';
import { FreigabeDialog } from './FreigabeDialog';

type Stand = { dok: FreigabeDok; zeilen: FreigabeAnzeige[]; abrufe: { name: string; erstmals: string; zuletzt: string; anzahl: number }[] };

export function FreigabenKarte({ dokumentId, onMeldung }: { dokumentId: string; onMeldung?: (text: string, fehler?: boolean) => void }) {
  const [stand, setStand] = useState<Stand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | 'neu' | 'aendern'>(null);
  const [entziehen, setEntziehen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [runde, setRunde] = useState(0);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);

  useEffect(() => {
    let aktiv = true;
    setFehler(null);
    void (async () => {
      try {
        const { data, error } = await supabase.from('dokumente')
          .select('id, titel, datei_name, objekt_id, mieter_id, eigentuemer_id, kategorie, dokument_typ').eq('id', dokumentId).maybeSingle();
        if (error) throw new Error(error.message);
        if (!data) return;
        const d = data as FreigabeDok & { datei_name: string | null };
        const anzeige = await ladeFreigabeAnzeige(dokumentId);
        if (aktiv) setStand({ dok: { ...d, titel: d.titel || d.datei_name || 'Dokument' }, ...anzeige });
      } catch (e) {
        if (aktiv) setFehler(e instanceof Error ? e.message : 'Freigaben konnten nicht geladen werden');
      }
    })();
    return () => { aktiv = false; };
  }, [dokumentId, runde]);

  if (fehler) return <span role="alert" style={{ fontSize: 12, color: 'var(--bad)' }}>Freigaben: {fehler}</span>;
  if (!stand) return null;
  const frei = stand.zeilen.length > 0;

  const allesEntziehen = async () => {
    setLaeuft(true);
    const f = await entziehe([dokumentId], null);
    setLaeuft(false); setEntziehen(false);
    if (f) { onMeldung?.(`Nicht entzogen: ${f}`, true); return; }
    onMeldung?.('Alle Freigaben entzogen');
    meldeFreigabenGeaendert();
    neuLaden();
  };

  return (
    <div className="flex flex-col" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--fill)' }}>
      <div className="flex items-center" style={{ gap: 10 }}>
        {frei ? <Globe size={18} weight="fill" style={{ color: 'var(--acc)', flexShrink: 0 }} /> : <LockSimple size={18} weight="fill" style={{ color: 'var(--ink3)', flexShrink: 0 }} />}
        <span className="flex-1" style={{ fontSize: 13, fontWeight: 800 }}>{frei ? 'Freigaben im Portal' : 'Nicht im Portal freigegeben'}</span>
      </div>
      {stand.zeilen.map(z => (
        <div key={z.freigabe.id} className="flex flex-col" style={{ gap: 1, paddingLeft: 28 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{z.text}{z.zusatz ? <span style={{ fontWeight: 500, color: 'var(--ink3)' }}> · {z.zusatz}</span> : null}</span>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
            seit {deDatum(z.freigabe.freigegeben_am)}{z.von ? ` · von ${z.von}` : ''}
          </span>
        </div>
      ))}
      {stand.abrufe.length > 0 && (
        <div className="flex flex-col" style={{ gap: 2, paddingLeft: 28 }}>
          <span className="flex items-center" style={{ gap: 5, fontSize: 11, fontWeight: 800, color: 'var(--ink3)' }}><Eye size={12} weight="bold" />Geöffnet</span>
          {stand.abrufe.map(a => (
            <span key={a.name + a.erstmals} style={{ fontSize: 12 }}>
              {a.name} · erstmals {deDatum(a.erstmals)}{a.anzahl > 1 ? ` · ${a.anzahl}× (zuletzt ${deDatum(a.zuletzt)})` : ''}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap justify-end" style={{ gap: 6 }}>
        {frei && <ImmoButton art="bad" disabled={laeuft} onClick={() => setEntziehen(true)}>Entziehen</ImmoButton>}
        {frei && <ImmoButton onClick={() => setDialog('aendern')}>Ändern</ImmoButton>}
        <ImmoButton art={frei ? undefined : 'akzent'} icon={Globe} onClick={() => setDialog('neu')}>{frei ? 'Weitere' : 'Freigeben'}</ImmoButton>
      </div>
      <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Es wird nichts verschickt.</span>

      {dialog && (
        <FreigabeDialog dokumente={[stand.dok]} aendern={dialog === 'aendern'} onSchliessen={() => setDialog(null)}
          onFertig={m => { setDialog(null); onMeldung?.(m); meldeFreigabenGeaendert(); neuLaden(); }} />
      )}
      {entziehen && (
        <BestaetigenDialog titel="Alle Freigaben entziehen?" text="Das Dokument ist danach in keinem Portal mehr sichtbar. Freigeben ist jederzeit wieder möglich."
          bestaetigenLabel="Entziehen" icon={LockSimple} laeuft={laeuft} onBestaetigen={() => { void allesEntziehen(); }} onAbbrechen={() => setEntziehen(false)} />
      )}
    </div>
  );
}
